// HUD + overlays. Button geometry is shared with input via getButtons() (same
// pattern as the other games). All colours come from the theme (CSS vars).
import { W, H } from './config';
import { Theme, Button, Score, Phase } from './types';

const TWO_PI = Math.PI * 2;

export function getButtons(phase: Phase): Record<string, Button> {
  const cx = W / 2;
  if (phase === 'menu') return { play: { x: cx - 150, y: 720, w: 300, h: 96 } };
  if (phase === 'running') return { close: { x: W - 86, y: 24, w: 62, h: 62 } };
  if (phase === 'paused') return {
    resume: { x: cx - 150, y: 600, w: 300, h: 92 },
    menu: { x: cx - 150, y: 716, w: 300, h: 84 },
    home: { x: cx - 150, y: 820, w: 300, h: 84 },
  };
  return { // gameover
    again: { x: cx - 150, y: 740, w: 300, h: 96 },
    menu: { x: cx - 150, y: 860, w: 300, h: 84 },
  };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function center(ctx: CanvasRenderingContext2D, txt: string, x: number, y: number, font: string, color: string): void {
  ctx.fillStyle = color; ctx.font = font; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillText(txt, x, y);
}

function button(ctx: CanvasRenderingContext2D, b: Button, label: string, theme: Theme, font = 'bold 40px system-ui', bg = theme.btnBg, tx = theme.btnTx): void {
  ctx.fillStyle = bg;
  roundRect(ctx, b.x, b.y, b.w, b.h, 18);
  ctx.fill();
  center(ctx, label, b.x + b.w / 2, b.y + b.h / 2 + 14, font, tx);
}

// ── Progress bar (left) ──
function drawBar(ctx: CanvasRenderingContext2D, theme: Theme, s: Score): void {
  const x = 12, y = 150, w = 46, h = H - 360;
  const frac = Math.max(0, Math.min(s.event / s.goal, 1));
  // frame
  ctx.fillStyle = theme.barFrame;
  roundRect(ctx, x - 4, y - 4, w + 8, h + 8, 14); ctx.fill();
  // trough
  ctx.fillStyle = theme.barTrough;
  roundRect(ctx, x, y, w, h, 10); ctx.fill();
  // fill (from bottom)
  const fh = h * frac;
  ctx.fillStyle = theme.barFill;
  roundRect(ctx, x, y + h - fh, w, fh, 10); ctx.fill();
  // marker
  const my = y + h - fh;
  ctx.fillStyle = theme.barMark;
  ctx.beginPath(); ctx.arc(x + w / 2, my, 14, 0, TWO_PI); ctx.fill();
  // star + points on top
  ctx.fillStyle = theme.badgeBg;
  ctx.beginPath(); ctx.arc(x + w / 2, y - 26, 26, 0, TWO_PI); ctx.fill();
  center(ctx, '★', x + w / 2, y - 16, 'bold 30px system-ui', theme.badgeTx);
  center(ctx, String(s.event), x + w / 2, y + h + 34, 'bold 26px system-ui', theme.text);
}

// ── Multiplier badge (top centre) ──
function drawBadge(ctx: CanvasRenderingContext2D, theme: Theme, s: Score, now: number): void {
  const bonus = s.bonusUntil > now;
  const mult = bonus ? 2 : 1;
  const cx = W / 2, y = 36, w = 150, h = 74;
  const pulse = bonus ? 1 + 0.05 * Math.sin(now / 90) : 1;
  ctx.save();
  ctx.translate(cx, y + h / 2);
  ctx.scale(pulse, pulse);
  ctx.fillStyle = theme.badgeBg;
  roundRect(ctx, -w / 2, -h / 2, w, h, 20); ctx.fill();
  center(ctx, `★ ×${mult}`, 0, 12, 'bold 40px system-ui', theme.badgeTx);
  ctx.restore();
  center(ctx, String(s.run), cx, y + h + 40, 'bold 34px system-ui', theme.text);
}

// ── Close (×) ──
function drawClose(ctx: CanvasRenderingContext2D, theme: Theme): void {
  const b = getButtons('running').close;
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2, r = b.w / 2;
  ctx.fillStyle = theme.closeBg;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = theme.textLight; ctx.lineWidth = 6; ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 13, cy - 13); ctx.lineTo(cx + 13, cy + 13);
  ctx.moveTo(cx + 13, cy - 13); ctx.lineTo(cx - 13, cy + 13);
  ctx.stroke();
  ctx.lineCap = 'butt';
}

export function drawHUD(ctx: CanvasRenderingContext2D, theme: Theme, s: Score, now: number): void {
  drawBar(ctx, theme, s);
  drawBadge(ctx, theme, s, now);
  drawClose(ctx, theme);
}

function overlay(ctx: CanvasRenderingContext2D, theme: Theme): void {
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);
}

export function drawMenu(ctx: CanvasRenderingContext2D, theme: Theme, s: Score): void {
  overlay(ctx, theme);
  center(ctx, 'HELIX', W / 2, 380, 'bold 120px system-ui', theme.textLight);
  center(ctx, 'DROP', W / 2, 510, 'bold 120px system-ui', theme.badgeBg);
  button(ctx, getButtons('menu').play, 'Играть', theme, 'bold 48px system-ui');
  center(ctx, `Лучший забег: ${s.best}`, W / 2, 900, '34px system-ui', theme.textLight);
  center(ctx, `Очки события: ${s.event}`, W / 2, 950, '30px system-ui', theme.textLight);
  center(ctx, 'Тяни пальцем влево-вправо — крути башню', W / 2, 1040, '26px system-ui', theme.textLight);
}

export function drawPause(ctx: CanvasRenderingContext2D, theme: Theme): void {
  overlay(ctx, theme);
  center(ctx, 'Пауза', W / 2, 470, 'bold 80px system-ui', theme.textLight);
  button(ctx, getButtons('paused').resume, 'Продолжить', theme);
  button(ctx, getButtons('paused').menu, 'В меню', theme, 'bold 36px system-ui');
  button(ctx, getButtons('paused').home, 'В хаб', theme, 'bold 36px system-ui', theme.badgeBg, theme.badgeTx);
}

export function drawGameOver(ctx: CanvasRenderingContext2D, theme: Theme, s: Score, isRecord: boolean): void {
  overlay(ctx, theme);
  center(ctx, 'Игра окончена', W / 2, 420, 'bold 64px system-ui', theme.textLight);
  center(ctx, String(s.run), W / 2, 580, 'bold 130px system-ui', theme.badgeBg);
  center(ctx, 'очки забега', W / 2, 630, '30px system-ui', theme.textLight);
  if (isRecord) center(ctx, '🏆 Рекорд!', W / 2, 690, 'bold 44px system-ui', theme.badgeBg);
  else center(ctx, `Рекорд: ${s.best}`, W / 2, 690, '32px system-ui', theme.textLight);
  button(ctx, getButtons('gameover').again, 'Ещё раз', theme);
  button(ctx, getButtons('gameover').menu, 'В меню', theme, 'bold 36px system-ui');
}
